import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-(--radius-sm) font-(family-name:--font-ui) font-medium uppercase tracking-[0.1em] text-xs min-h-11 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-40 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-surface-elevated text-text border border-border hover:bg-surface-hover hover:border-accent',
        primary: 'bg-accent text-inverse border border-accent hover:bg-accent-hover hover:border-accent-hover',
        ghost: 'bg-transparent text-text hover:bg-surface-hover',
        danger: 'bg-accent-danger text-inverse border border-accent-danger',
      },
      size: {
        sm: 'px-3 py-1.5 text-[0.7rem] min-h-9',
        md: 'px-4 py-2',
        lg: 'px-6 py-3 text-sm',
        icon: 'h-11 w-11 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'md' },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />;
  }
);
Button.displayName = 'Button';
